import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, LogIn, LogOut as LogOutIcon, Coffee, UtensilsCrossed,
  CheckCircle2, AlertTriangle, RefreshCw, Search, Clock4, Camera,
} from "lucide-react";
import FaceCapture from "../components/FaceCapture";

// Public clock-in page reached by scanning the rotating QR.
// NO authentication required. URL: /clock-in?t=<jwt>
// Flow: validate token → show worker list → worker taps name → context-aware
// "next action" buttons → record event → confirmation.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

// Persistent device id — first time a phone visits the clock-in page we
// generate a random uuid and stash it in localStorage. This is what the
// server uses to bind a worker to a single phone for the entire shift's
// lifetime (the phone keeps using the same id across QR rotations).
function getDeviceId() {
  try {
    let v = localStorage.getItem("mercy_device_id");
    if (!v) {
      // RFC4122-ish uuid w/o crypto.randomUUID (Safari < 15 lacks it)
      v = "dev-" + Math.random().toString(36).slice(2) +
          "-" + Date.now().toString(36) +
          "-" + Math.random().toString(36).slice(2, 8);
      localStorage.setItem("mercy_device_id", v);
    }
    return v;
  } catch {
    return "dev-no-ls-" + Math.random().toString(36).slice(2);
  }
}

const ACTION_META = {
  in:        { label: "Clock in",          icon: LogIn,             color: "emerald" },
  lunch_out: { label: "Going for lunch",   icon: UtensilsCrossed,   color: "amber" },
  lunch_in:  { label: "Back from lunch",   icon: UtensilsCrossed,   color: "emerald" },
  break_out: { label: "Going on break",    icon: Coffee,            color: "sky" },
  break_in:  { label: "Back from break",   icon: Coffee,            color: "emerald" },
  out:       { label: "End shift",         icon: LogOutIcon,        color: "rose" },
};

const COLOR_CLS = {
  emerald: "bg-emerald-500 hover:bg-emerald-400 text-emerald-950",
  amber:   "bg-amber-500 hover:bg-amber-400 text-amber-950",
  sky:     "bg-sky-500 hover:bg-sky-400 text-sky-950",
  rose:    "bg-rose-500 hover:bg-rose-400 text-rose-950",
};

export default function ClockIn() {
  const [params] = useSearchParams();
  const token = params.get("t") || params.get("token") || "";
  // Generate device id once per device (stable across page reloads & QR rotations)
  const [deviceId] = useState(() => getDeviceId());

  const [phase, setPhase] = useState("loading"); // loading | pick | confirm | face | done | expired | error | blocked
  const [errMsg, setErrMsg] = useState("");
  const [shift, setShift] = useState(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [nextOptions, setNextOptions] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  // Face-recognition state: which action the worker just chose, and the
  // server's view of their enrolment + monthly fallback quota.
  const [pendingKind, setPendingKind] = useState(null);
  const [faceStatus, setFaceStatus] = useState(null); // {enrolled, fallbacks_remaining, …}

  // ---------- Load shift workers ----------
  const lookup = useCallback(async () => {
    if (!token) {
      setPhase("error");
      setErrMsg("No QR token in the URL. Please re-scan the QR.");
      return;
    }
    setPhase("loading");
    try {
      const { data } = await publicApi.post("/clock-in/lookup", { token, device_id: deviceId });
      setShift(data);
      setPhase("pick");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) {
        setPhase("expired");
      } else {
        setPhase("error");
        setErrMsg(e?.response?.data?.detail || "Could not load. Please try again.");
      }
    }
  }, [token, deviceId]);

  useEffect(() => { lookup(); }, [lookup]);

  // ---------- Scan ----------
  // Low-level — submit a scan with full body. Called by the face flow with
  // an embedding or with face_action='fallback'. Throws on non-OK.
  const submitScan = async ({ kind, faceAction, embedding, blinks }) => {
    const body = {
      token,
      worker_id: picked.id,
      device_id: deviceId,
      kind,
      face_action: faceAction,
    };
    if (embedding) body.face_embedding = embedding;
    if (blinks != null) body.liveness = { blinks };
    const { data } = await publicApi.post("/clock-in/scan", body);
    setLastEvent({
      kind: data.event.kind,
      time: data.event.timestamp,
      worker: data.worker_name,
      bound_now: data.bound_now,
      face_enrolled_now: data.face_enrolled_now,
      fallback_used: data.fallback_used,
      face_distance: data.event?.face_distance,
    });
    setNextOptions(data.next_options || []);
    setPhase("done");
  };

  // High-level — start the face flow for a chosen kind. We fetch the face
  // status first so the FaceCapture component knows whether to enrol or
  // verify and how many fallbacks remain.
  const startScan = async (kind) => {
    if (!picked) return;
    setPendingKind(kind);
    try {
      const { data: status } = await publicApi.post("/clock-in/face-status", {
        token, worker_id: picked.id,
      });
      setFaceStatus(status);
      setPhase("face");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) {
        setPhase("expired");
      } else {
        alert(e?.response?.data?.detail || "Could not check face status. Try again.");
      }
    }
  };

  // After FaceCapture emits an embedding, send the scan. If verify returns
  // a 422 (mismatch) we let the user fall back to QR mode — counting against
  // their monthly quota.
  const onFaceCaptured = async ({ embedding, blinks }) => {
    const action = faceStatus?.enrolled ? "verify" : "enrol";
    try {
      await submitScan({ kind: pendingKind, faceAction: action, embedding, blinks });
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 422 && detail && detail.can_fallback) {
        // Mismatch with quota remaining — show inline fallback prompt
        setFaceStatus((s) => ({
          ...(s || {}),
          mismatch: true,
          fallbacks_remaining: detail.fallbacks_remaining,
          last_distance: detail.distance,
        }));
        // Stay on `face` phase; FaceCapture is unmounted so the UI shows fallback
        setPhase("face-mismatch");
      } else if (code === 410) {
        setPhase("expired");
      } else if (code === 409 && /different phone/i.test(typeof detail === "string" ? detail : "")) {
        setErrMsg(typeof detail === "string" ? detail : "Wrong phone");
        setPhase("blocked");
      } else {
        alert(typeof detail === "string" ? detail : "Could not record. Try again.");
        setPhase("confirm");
      }
    }
  };

  const onUseFallback = async () => {
    try {
      await submitScan({ kind: pendingKind, faceAction: "fallback" });
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 429) {
        alert(typeof detail === "string" ? detail : "No fallback scans remaining.");
        setPhase("confirm");
      } else if (code === 410) {
        setPhase("expired");
      } else {
        alert(typeof detail === "string" ? detail : "Could not record. Try again.");
        setPhase("confirm");
      }
    }
  };

  // Filter workers by search query
  const filteredWorkers = (shift?.workers || []).filter(
    (w) => !query || w.name.toLowerCase().includes(query.toLowerCase())
  );

  // ---------- RENDER ----------
  if (phase === "loading") {
    return <Frame><div className="text-slate-400 text-center py-20">Loading…</div></Frame>;
  }
  if (phase === "expired") {
    return (
      <Frame>
        <Center>
          <AlertTriangle className="w-12 h-12 text-amber-400 mb-3" />
          <h2 className="text-xl font-bold text-amber-200">QR expired</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-xs">
            This QR rotates every 15 minutes for security.
            Please ask your supervisor to refresh the display, then scan the new QR.
          </p>
        </Center>
      </Frame>
    );
  }
  if (phase === "error") {
    return (
      <Frame>
        <Center>
          <AlertTriangle className="w-12 h-12 text-red-400 mb-3" />
          <h2 className="text-xl font-bold text-red-200">Something went wrong</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-xs">{errMsg}</p>
          <button onClick={lookup} className="mt-4 px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm">
            <RefreshCw className="w-4 h-4 inline mr-1" /> Try again
          </button>
        </Center>
      </Frame>
    );
  }

  if (phase === "blocked") {
    return (
      <Frame>
        <Center>
          <AlertTriangle className="w-12 h-12 text-red-400 mb-3" />
          <h2 className="text-xl font-bold text-red-200">Wrong phone</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-xs">
            <strong className="text-slate-200">{picked?.name}</strong> is registered to another phone.
            <br /><br />
            Ask your supervisor to reset, then scan again from your own phone.
          </p>
          <button onClick={() => { setPicked(null); setPhase("pick"); }}
            className="mt-5 px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-sm">
            ← Pick someone else
          </button>
        </Center>
      </Frame>
    );
  }

  if (phase === "pick") {
    return (
      <Frame>
        <ShiftHeader shift={shift} />
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              type="text"
              placeholder="Type your name to find yourself…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-[#0a0e1a] border border-slate-800 rounded-lg pl-10 pr-3 py-3 text-base placeholder-slate-600 focus:border-emerald-500 outline-none"
              data-testid="clockin-search"
            />
          </div>
          {/* If any worker on THIS device is bound to me, surface them on top */}
          {shift?.workers?.some((w) => w.device_state === "mine") && !query && (
            <div className="mt-2 text-[11px] text-emerald-400">
              ✓ Your phone is registered to a worker — tap that name below.
            </div>
          )}
        </div>
        <div className="px-3 pb-3 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {filteredWorkers.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8">
              No match. Check spelling or try fewer letters.
            </div>
          )}
          {filteredWorkers.map((w) => {
            const isDone = w.last_kind === "out";
            const otherDevice = w.device_state === "other";
            const mine = w.device_state === "mine";
            let meta;
            let disabled = false;
            if (otherDevice) {
              meta = "🔒 Registered to another phone";
              disabled = true;
            } else if (mine) {
              meta = "📱 This is your phone";
            } else if (isDone) {
              meta = "✓ Already ended for today";
              disabled = true;
            } else if (w.last_kind) {
              meta = `Last: ${ACTION_META[w.last_kind]?.label || w.last_kind}`;
            } else {
              meta = "Tap to claim this name to your phone";
            }
            return (
              <button
                key={w.id}
                onClick={() => {
                  if (disabled) return;
                  setPicked(w);
                  setPhase("confirm");
                }}
                disabled={disabled}
                data-testid={`clockin-worker-${w.id}`}
                className={`w-full text-left px-4 py-3 mb-1.5 rounded-lg border flex items-center gap-3 transition
                  ${disabled
                    ? "border-slate-800 bg-slate-900/30 opacity-50 cursor-not-allowed"
                    : mine
                      ? "border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500/30 hover:bg-emerald-500/20"
                      : "border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/5 active:scale-[0.98]"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium truncate">{w.name}</div>
                  <div className={`text-[11px] mt-0.5 ${otherDevice ? "text-red-400" : mine ? "text-emerald-300" : "text-slate-500"}`}>
                    {meta}
                  </div>
                </div>
                {w.code && <span className="text-[10px] num text-slate-600">{w.code}</span>}
              </button>
            );
          })}
        </div>
        <Footer expiresAt={shift?.expires_at} />
      </Frame>
    );
  }

  if (phase === "confirm") {
    // figure out what they can do next based on last action
    const last = picked?.last_kind;
    const options = nextOptionsFor(last);
    return (
      <Frame>
        <ShiftHeader shift={shift} />
        <div className="px-5 py-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Hello,</div>
          <h2 className="text-2xl font-bold mt-1">{picked.name}</h2>
          {last && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/60 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Last action: <span className="font-medium">{ACTION_META[last]?.label || last}</span>
            </div>
          )}

          <div className="mt-6 text-xs uppercase tracking-wider text-slate-500">What's next?</div>
          <div className="mt-3 space-y-2">
            {options.length === 0 && (
              <div className="text-sm text-slate-400 py-4 px-4 bg-slate-900/50 rounded-lg">
                ✅ You're already done for today. See you tomorrow!
              </div>
            )}
            {options.map((k) => {
              const M = ACTION_META[k];
              const Icon = M.icon;
              return (
                <button key={k} onClick={() => startScan(k)}
                  data-testid={`clockin-action-${k}`}
                  className={`w-full px-4 py-4 rounded-xl font-semibold text-base flex items-center gap-3 active:scale-[0.98] transition shadow-lg ${COLOR_CLS[M.color]}`}>
                  <Icon className="w-5 h-5" />
                  {M.label}
                </button>
              );
            })}
          </div>

          <button onClick={() => { setPicked(null); setPhase("pick"); }}
            className="mt-6 text-sm text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
            data-testid="clockin-not-me">
            ← Not me, pick someone else
          </button>
        </div>
        <Footer expiresAt={shift?.expires_at} />
      </Frame>
    );
  }

  if (phase === "face") {
    return (
      <Frame>
        <ShiftHeader shift={shift} />
        <FaceCapture
          mode={faceStatus?.enrolled ? "verify" : "enrol"}
          workerName={picked?.name || ""}
          fallbacksRemaining={faceStatus?.fallbacks_remaining ?? 0}
          onComplete={onFaceCaptured}
          onUseFallback={onUseFallback}
          onCancel={() => { setPhase("confirm"); setPendingKind(null); setFaceStatus(null); }}
        />
        <Footer expiresAt={shift?.expires_at} />
      </Frame>
    );
  }

  if (phase === "face-mismatch") {
    const left = faceStatus?.fallbacks_remaining ?? 0;
    return (
      <Frame>
        <ShiftHeader shift={shift} />
        <div className="px-5 py-6 flex flex-col items-center text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mb-3" />
          <h2 className="text-lg font-bold text-amber-200">Face didn't match</h2>
          <p className="text-xs text-slate-400 mt-2 max-w-xs">
            We couldn't confirm it's <strong>{picked?.name}</strong>. This can happen with
            bad lighting, sunglasses, or a recent appearance change.
          </p>
          {left > 0 ? (
            <>
              <p className="text-xs text-slate-300 mt-3 max-w-xs">
                You have <strong className="text-amber-300">{left}</strong> QR-only scan{left === 1 ? "" : "s"} left this month.
                Continue without face check?
              </p>
              <div className="mt-5 w-full max-w-xs space-y-2">
                <button onClick={onUseFallback}
                  className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 text-sm font-semibold"
                  data-testid="face-mismatch-fallback">
                  <Camera className="w-4 h-4 inline mr-1.5" />
                  Continue ({left} left)
                </button>
                <button onClick={() => { setPhase("face"); }}
                  className="w-full py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm"
                  data-testid="face-mismatch-retry">
                  <RefreshCw className="w-3.5 h-3.5 inline mr-1" />
                  Try face again
                </button>
              </div>
            </>
          ) : (
            <div className="mt-4 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 max-w-xs">
              You've used all 3 QR fallbacks for this month. Please ask your
              supervisor to reset your face enrolment.
            </div>
          )}
          <button onClick={() => { setPhase("confirm"); setPendingKind(null); }}
            className="mt-5 text-xs text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline">
            ← Back
          </button>
        </div>
        <Footer expiresAt={shift?.expires_at} />
      </Frame>
    );
  }

  if (phase === "done") {
    const M = ACTION_META[lastEvent?.kind];
    const time = lastEvent?.time
      ? new Date(lastEvent.time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
      : "";
    return (
      <Frame>
        <ShiftHeader shift={shift} />
        <div className="px-5 py-8 flex flex-col items-center text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mb-4 drop-shadow-[0_0_20px_rgba(52,211,153,0.5)]" />
          <h2 className="text-2xl font-bold text-emerald-200">{M?.label} recorded</h2>
          <div className="text-3xl font-bold num mt-2">{time}</div>
          <div className="text-sm text-slate-400 mt-1">{lastEvent?.worker}</div>
          {lastEvent?.bound_now && (
            <div className="mt-3 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 max-w-xs">
              📱 This phone is now registered to <strong>{lastEvent.worker}</strong>.
              Only this phone can clock in/out for them. Tell your supervisor if you lose this phone.
            </div>
          )}
          {lastEvent?.face_enrolled_now && (
            <div className="mt-2 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 max-w-xs">
              ✓ Your face is now saved as your baseline. Future scans will verify automatically.
            </div>
          )}
          {lastEvent?.fallback_used && (
            <div className="mt-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 max-w-xs">
              ⚠️ Recorded without face check. Ask your supervisor to refresh your face baseline soon.
            </div>
          )}

          {nextOptions.length > 0 ? (
            <button onClick={() => { setPhase("pick"); setPicked(null); setQuery(""); setPendingKind(null); setFaceStatus(null); }}
              className="mt-8 px-6 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm font-medium"
              data-testid="clockin-another">
              Done — next person
            </button>
          ) : (
            <div className="mt-6 text-sm text-slate-400">Have a good rest of your day!</div>
          )}
        </div>
        <Footer expiresAt={shift?.expires_at} />
      </Frame>
    );
  }
  return null;
}

// next-state options matching backend logic
function nextOptionsFor(lastKind) {
  if (!lastKind) return ["in"];
  if (lastKind === "in") return ["lunch_out", "break_out", "out"];
  if (lastKind === "lunch_out") return ["lunch_in"];
  if (lastKind === "lunch_in") return ["lunch_out", "break_out", "out"];
  if (lastKind === "break_out") return ["break_in"];
  if (lastKind === "break_in") return ["lunch_out", "break_out", "out"];
  return [];
}

// ============================================================================
// Layout helpers
// ============================================================================
function Frame({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0a0e1a] to-[#0c1322] text-slate-100 flex flex-col items-center justify-center px-3 py-4">
      <div className="w-full max-w-md bg-[#0f1626] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="border-b border-slate-800 px-4 py-3 flex items-center gap-2 bg-[#0a0e1a]">
          <div className="w-6 h-6 rounded bg-red-600/90 flex items-center justify-center">
            <ShieldCheck className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs font-semibold tracking-tight">Mercy Management — Clock-in</span>
        </div>
        {children}
      </div>
      <p className="mt-4 text-[10px] text-slate-600 num">mercymy.com</p>
    </div>
  );
}

function ShiftHeader({ shift }) {
  if (!shift) return null;
  return (
    <div className="px-5 pt-4 pb-2 border-b border-slate-800/60">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 num">{shift.shift_label}</div>
      <div className="text-base font-semibold mt-0.5">{shift.company_name}</div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="px-5 py-10 flex flex-col items-center text-center">{children}</div>
  );
}

function Footer({ expiresAt }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="border-t border-slate-800/60 px-4 py-2 text-[10px] text-slate-500 num flex items-center justify-between">
      <span>QR valid · refreshes every 15 min</span>
      <span className="flex items-center gap-1"><Clock4 className="w-3 h-3" /> {mm}:{ss}</span>
    </div>
  );
}
